// Chat-timeline render strings — the readiness card, proposal cards, the link
// selector (now only inside CreateIssueProposal), and the small status/notice
// surfaces in apps/web/src/chat/render/*. Reusable verbs/labels
// (Send/Add/Accept, field.*.cardLabel, readiness.verdict.*) come from
// common/fields/readiness; this section holds only the copy specific to these
// render surfaces.

import type { MsgSection } from "./types.js";

export const chatRender: MsgSection = {
  // ── ReadinessCard ──────────────────────────────────────────────────────
  "chatRender.readinessCardTitle": {
    en: "Readiness card · #{iid}",
    de: "Readiness-Karte · #{iid}",
  },
  "chatRender.ready": { en: "Ready", de: "Bereit" },
  // The story review's criteria, by what they check — never the judge's ids.
  "chatRender.criterion.independent": { en: "Independent", de: "Unabhängig" },
  "chatRender.criterion.negotiable": { en: "Negotiable", de: "Verhandelbar" },
  "chatRender.criterion.valuable": { en: "Valuable", de: "Wertvoll" },
  "chatRender.criterion.estimatable": { en: "Estimable", de: "Schätzbar" },
  "chatRender.criterion.small": { en: "Small", de: "Klein" },
  "chatRender.criterion.testable": { en: "Testable", de: "Testbar" },
  "chatRender.criterion.conceptuallySound": {
    en: "Reads as a story",
    de: "Liest sich als Story",
  },
  // What the user's own bubble shows for the accept click on #{iid}. The sent
  // message is an English instruction carrying the acceptToken; this is what
  // the user did.
  "chatRender.acceptedChanges": {
    en: "Apply the changes to #{iid}.",
    de: "Änderungen in #{iid} übernehmen.",
  },
  // In place of the English refine seed ("Let's refine issue #N: … Ground in
  // the codebase first …"), which the user never typed.
  "chatRender.refineStarted": {
    en: "Refine #{iid}.",
    de: "#{iid} verfeinern.",
  },
  "chatRender.noMilestones": {
    en: "No milestones in this project.",
    de: "Keine Meilensteine in diesem Projekt.",
  },
  "chatRender.milestonesError": {
    // Byte-identical to the original (curly apostrophe, "load milestones").
    en: "Couldn’t load milestones.",
    de: "Meilensteine liessen sich nicht laden.",
  },
  "chatRender.milestoneClosedSuffix": { en: " (closed)", de: " (geschlossen)" },
  "chatRender.dorPass": { en: "Ready to start", de: "Startbereit" },
  "chatRender.dorFail": { en: "Not ready to start — missing: {missing}", de: "Noch nicht startbereit — fehlt: {missing}" },
  "chatRender.dodPass": { en: "Meets Definition of Done", de: "Erfüllt Definition of Done" },
  "chatRender.dodFail": { en: "Doesn't meet Definition of Done yet", de: "Erfüllt Definition of Done noch nicht" },
  "chatRender.investScore": { en: "Story quality {score}/100", de: "Story-Qualität {score}/100" },
  "chatRender.acceptChanges": { en: "Accept changes", de: "Änderungen übernehmen" },
  // The badge when nothing is green yet but the card holds no non-green slot of
  // its own — the DoR verdict is naming fields the card has no row for. Without
  // it the badge read "0 gaps" over "Not ready to start — missing: …".
  "chatRender.notReady": { en: "Not ready", de: "Noch nicht bereit" },
  // A refused apply, on the surface it was refused on. Same three cases the
  // refine flow's own screen has copy for; the server's string rides demoted
  // underneath, whichever it is.
  "chatRender.applyFail.headline": {
    en: "Not written to the issue",
    de: "Nicht ins Issue geschrieben",
  },
  "chatRender.applyFail.alreadyApplied": {
    en: "This issue was already written from this refinement, and the card has changed since. Open the issue and refine it again to write the newer version.",
    de: "Dieses Issue wurde aus dieser Verfeinerung bereits geschrieben, und die Karte hat sich seither geändert. Öffne das Issue und verfeinere es erneut, um die neuere Fassung zu schreiben.",
  },
  "chatRender.applyFail.editPending": {
    en: "Your edit hasn’t been folded into the card yet — wait a moment, then accept again.",
    de: "Deine Änderung ist noch nicht in die Karte eingearbeitet — warte kurz und übernimm dann erneut.",
  },
  "chatRender.applyFail.generic": {
    en: "The issue was not changed. Nothing was written.",
    de: "Das Issue wurde nicht geändert. Es wurde nichts geschrieben.",
  },
  "chatRender.applyFail.noRetry": {
    en: "Refine again to write a newer version.",
    de: "Verfeinere erneut, um eine neuere Fassung zu schreiben.",
  },

  // ── AssistantTurn (loom-confirm Accept block) ──────────────────────────
  "chatRender.confirmationRequired": {
    en: "Confirmation required",
    de: "Bestätigung erforderlich",
  },
  "chatRender.acceptChangesSent": {
    en: "Sent — waiting for the agent…",
    de: "Gesendet — der Agent ist dran…",
  },

  // ── BacklogPatchList (removed) ─────────────────────────────────────────
  // GONE with the component. `groom_backlog`'s Confirm control told the user
  // "Applied N patches." while nothing left the browser, so the list and its
  // nine strings were removed rather than translated further; the workflow is
  // deregistered. Backlog-wide grooming, if it comes back, needs a token-gated
  // write beside `apply_update_issue` and its own copy.

  // ── ProposalCard ───────────────────────────────────────────────────────
  "chatRender.applied": { en: "Applied.", de: "Übernommen." },
  "chatRender.rejected": { en: "Rejected.", de: "Abgelehnt." },
  "chatRender.moderniseLegacy": {
    en: "Modernise legacy issue !{iid}",
    de: "Altes Issue !{iid} auffrischen",
  },
  "chatRender.titleField": { en: "title:", de: "Titel:" },
  "chatRender.descriptionField": { en: "description:", de: "Beschreibung:" },
  "chatRender.descriptionChars": { en: "({count} chars)", de: "({count} Zeichen)" },
  "chatRender.addLabels": { en: "+ labels:", de: "+ Labels:" },
  "chatRender.removeLabels": { en: "- labels:", de: "- Labels:" },
  "chatRender.missingDorInline": { en: "missing:", de: "fehlt:" },
  "chatRender.dorHints": { en: "What's missing to start — issue !{iid}", de: "Was zum Start fehlt — Issue !{iid}" },
  "chatRender.dodHints": { en: "What's missing to finish — issue !{iid}", de: "Was zum Abschluss fehlt — Issue !{iid}" },
  "chatRender.missingInline": { en: "missing:", de: "fehlend:" },
  "chatRender.createIssueProposal": {
    en: "Create issue: {title}",
    de: "Issue erstellen: {title}",
  },
  "chatRender.labelsField": { en: "labels:", de: "Labels:" },
  "chatRender.linksField": { en: "links:", de: "Verknüpfungen:" },
  // NOTE: "N candidate(s)" keeps its inline English plural ternary — no fitting
  // plural.* key. Flagged.

  // ── CreateIssueProposal ────────────────────────────────────────────────
  "chatRender.newIssue": { en: "New issue", de: "Neues Issue" },
  "chatRender.confirmNoLinks": {
    en: "Confirm to create the issue without proposed links.",
    de: "Bestätige, um das Issue ohne die vorgeschlagenen Verknüpfungen zu erstellen.",
  },
  // NOTE: the "apply N link(s)" confirm-to-create sentence keeps its inline
  // English plural ternary in the component — no fitting plural.* key. Flagged.

  // ── SelectorBlock ──────────────────────────────────────────────────────
  // NOTE: "Selected N link(s)." keeps its inline English plural ternary — no
  // fitting plural.* key. Flagged.
  "chatRender.linkCandidates": { en: "Link candidates", de: "Verknüpfungskandidaten" },
  "chatRender.filterCandidates": {
    en: "Filter candidates…",
    de: "Kandidaten eingrenzen…",
  },
  "chatRender.confirmSelection": { en: "Confirm selection", de: "Auswahl bestätigen" },

  // ── BlockNotice ────────────────────────────────────────────────────────
  "chatRender.blocked": { en: "blocked: {reason}", de: "blockiert: {reason}" },
  "chatRender.missing": { en: "Missing:", de: "Fehlend:" },
  "chatRender.qualityHints": { en: "Quality hints:", de: "Qualitätshinweise:" },

  // ── LifecycleNotice ────────────────────────────────────────────────────
  "chatRender.lifecycleContinue": { en: "continue", de: "fortfahren" },

  // ── BudgetExhausted ────────────────────────────────────────────────────
  "chatRender.budgetExhausted": {
    en: "Budget exhausted ({label}): {used} of {limit} consumed.",
    de: "Budget aufgebraucht ({label}): {used} von {limit} genutzt.",
  },

  // ── ToolsUnavailable ───────────────────────────────────────────────────
  // Deliberately says nothing about MCP, bearers or tool wiring — the user
  // needs the consequence and the way out, not the mechanism.
  "chatRender.toolsUnavailable": {
    en: "Loom lost access to your project and can't finish this session. Start a new one to pick up where you left off.",
    de: "Loom hat den Zugriff auf dein Projekt verloren und kann diese Sitzung nicht abschliessen. Starte eine neue, um dort weiterzumachen, wo du aufgehört hast.",
  },

  // ── CodingProgress ─────────────────────────────────────────────────────
  "chatRender.fileRead": { en: "Read", de: "Gelesen" },
  "chatRender.fileEdited": { en: "Edited", de: "Bearbeitet" },
  "chatRender.fileCreated": { en: "Created", de: "Erstellt" },
  "chatRender.committed": { en: "Committed: {message}", de: "Committet: {message}" },

  // ── RoleSpawnBoundary ──────────────────────────────────────────────────
  "chatRender.subAgent": { en: "Sub-agent · {role}", de: "Unter-Agent · {role}" },
  "chatRender.hideTranscript": { en: "Hide transcript", de: "Transkript ausblenden" },
  "chatRender.showTranscript": { en: "Show transcript", de: "Transkript anzeigen" },
  "chatRender.returned": { en: "Returned:", de: "Zurückgegeben:" },

  // ── ToolCallCard ───────────────────────────────────────────────────────
  "chatRender.toolFallback": { en: "(tool)", de: "(Tool)" },
  "chatRender.statusRunning": { en: "running", de: "läuft" },
  "chatRender.statusSucceeded": { en: "succeeded", de: "erfolgreich" },
  "chatRender.statusFailed": { en: "failed", de: "fehlgeschlagen" },
  "chatRender.inputLabel": { en: "Input", de: "Eingabe" },
  "chatRender.outputLabel": { en: "Output", de: "Ausgabe" },

  // ── ErrorToast ─────────────────────────────────────────────────────────
  "chatRender.sessionEnded": { en: "Session ended: ", de: "Sitzung beendet: " },

  // ── AssistantTurn header ───────────────────────────────────────────────
  "chatRender.assistant": { en: "Assistant", de: "Assistent" },
};
